import { type ReactNode } from "react";
import type { components } from "@oathera/contract/api";
import { StatusBadge, type StatusLevel } from "../index.ts";
import { isoAttr, formatDateTime, formatTimeOfDay } from "../../lib/datetime.ts";

/**
 * AlertsPanel — compact firing/recent alerts list for the observability kit (observability-ui task
 * 1.5; design §Components and Interfaces #4). It generalises the Overview's `FiringAlerts` region:
 * same severity → StatusBadge mapping and the same honest empty state, but presentational only
 * (props in, no stream read, no routing) so every section (Overview, Operations) can reuse it.
 *
 * Each row is a severity pill (StatusBadge: glyph + text + colour, never colour alone — design 12.4),
 * the alert title, its target, and the time it fired/last changed. A row is a link only when the
 * item carries an `href`; otherwise it is a static, non-interactive list item. The list is a
 * labelled region with a heading and is keyboard-navigable through its native anchors (Requirement
 * 1.3). Colours come from the theme tokens via `observability.css` and the StatusBadge, so the panel
 * renders correctly in both themes with no code change (Requirement 2.1, 2.3).
 *
 * The empty state is honest (Requirement 1.2): when there are no items the panel says so rather than
 * rendering a fabricated row. No Netdata/GPL code is copied — this is a native rebuild.
 */

type AlertSeverity = components["schemas"]["AlertSeverity"];

/** A single alert row. UI-only view model derived from the screens' existing alert reads. */
export interface AlertsPanelItem {
  /** Stable key for the row (e.g. the rule id or alert id). */
  readonly id: string;
  /** Severity drives the pill. `null`/absent reads as unknown (grey) — never a healthy green. */
  readonly severity: AlertSeverity | null;
  /** The alert's headline (e.g. the rule name). */
  readonly title: string;
  /** What the alert is about (e.g. the service/source/target label). Optional. */
  readonly target?: string | null;
  /** When it fired / last changed, as Unix-ms. Optional; rendered safely when present. */
  readonly at?: number | null;
  /** A detail route for the row. When present the row is a link; otherwise it is static. */
  readonly href?: string | null;
}

export interface AlertsPanelProps {
  /** The alerts to list. An empty array renders the honest empty state. */
  readonly items: readonly AlertsPanelItem[];
  /**
   * The total number of matching alerts (may exceed `items.length` when the list is capped). Shown
   * as a count next to the heading. Defaults to `items.length` when omitted.
   */
  readonly total?: number;
  /** The region heading. Defaults to "Alerts". */
  readonly heading?: string;
}

export function AlertsPanel(props: AlertsPanelProps): ReactNode {
  const { items } = props;
  const heading = props.heading ?? "Alerts";
  const total = props.total ?? items.length;

  return (
    <section className="obs-panel obs-alerts" aria-label={heading}>
      <header className="obs-panel__header">
        <h2 className="obs-panel__title">
          {heading}
          {total > 0 && <span className="obs-alerts__count"> {total}</span>}
        </h2>
      </header>
      {items.length === 0 ? (
        <div className="obs-state" data-kind="empty">
          <strong>No alerts</strong>
          <span className="obs-state__hint">
            Nothing is firing in the current window. This updates as soon as a rule fires.
          </span>
        </div>
      ) : (
        <ul className="obs-alerts__list">
          {items.map((item) => (
            <li key={item.id}>
              <AlertRow item={item} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A single alert row. Rendered as an anchor when the item has an `href` (keyboard-navigable,
 * drills to the detail route) and as a static element otherwise, so a panel without routes is still
 * a correct, non-interactive list.
 */
function AlertRow(props: { readonly item: AlertsPanelItem }): ReactNode {
  const { item } = props;
  const word = severityWord(item.severity);
  const target = item.target?.trim() ? item.target : null;
  const label =
    `${word} alert: ${item.title}` +
    (target === null ? "" : ` — ${target}`) +
    (item.at == null ? "" : `, ${formatDateTime(item.at)}`) +
    (item.href ? ". Open alert." : "");

  const inner = (
    <>
      <StatusBadge level={severityLevel(item.severity)} label={word} size="sm" />
      <span className="obs-alerts__title">{item.title}</span>
      {target !== null && <span className="obs-alerts__target">{target}</span>}
      {item.at != null && (
        <time className="obs-alerts__time" dateTime={isoAttr(item.at)}>
          {formatTimeOfDay(item.at)}
        </time>
      )}
    </>
  );

  if (item.href) {
    return (
      <a className="obs-alerts__row" href={item.href} aria-label={label}>
        {inner}
      </a>
    );
  }
  return (
    <div className="obs-alerts__row" data-static="true" aria-label={label} role="group">
      {inner}
    </div>
  );
}

/** Map an alert severity onto a status level. A missing severity is unknown (grey, never green). */
function severityLevel(severity: AlertSeverity | null): StatusLevel {
  switch (severity) {
    case "critical":
      return "error";
    case "warning":
      return "warn";
    case "info":
      return "neutral";
    default:
      return "unknown";
  }
}

/** The visible/accessible severity word. A missing severity reads as a neutral "Firing". */
function severityWord(severity: AlertSeverity | null): string {
  switch (severity) {
    case "critical":
      return "Critical";
    case "warning":
      return "Warning";
    case "info":
      return "Info";
    default:
      return "Firing";
  }
}
